import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

type CreateTemplateResponse = {
  code: number;
  id: UUID;
  requestId: string;
};

type CreateTemplatePayload = {
  body: Record<string, any>;
  companyId?: UUID;
  defaultTemplate?: boolean;
  name: string;
};

/**
 * Creates a template.
 *
 * @param {CreateTemplatePayload} templateData - An object containing the template details to be created.
 * @returns {Promise<CreateTemplateResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Template/operation/upload_dln_template_dln_template_post
 */
export const createTemplate = async (templateData: CreateTemplatePayload) => {
  const { body, ...params } = templateData;

  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.CREATE_TEMPLATE(), {
      json: body,
      searchParams: params,
    })
    .json<CreateTemplateResponse>();

  return response;
};

/**
 * Custom hook for creating a template using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: CreateTemplatePayload) =>
      createTemplate(templateData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getTemplates(),
      });
    },
  });
};
